import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import './DashboardAnalytics.css';

const DashboardAnalytics = ({ dataLoading, setDataLoading }) => {
  const [analytics, setAnalytics] = useState({
    topSchoolsByUsers: [],
    topSchoolsByProviders: [],
    mostUsedProviders: [],
    totalStats: {
      totalSchools: 0,
      totalUsers: 0,
      totalProviders: 0,
      averageUsersPerSchool: 0
    }
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [schoolsResult, usersResult, providersResult] = await Promise.all([
        supabase.from('schools').select('*'),
        supabase.from('users').select('*'),
        supabase.from('providers').select('*')
      ]);

      const schools = schoolsResult.data || [];
      const users = usersResult.data || [];
      const providers = providersResult.data || [];

      // Calculate analytics
      const analyticsData = calculateAnalytics(schools, users, providers);
      setAnalytics(analyticsData);
      
    } catch (error) {
      } finally {
      setLoading(false);
    }
  };

  const calculateAnalytics = (schools, users, providers) => {
    // Normalize IDs to strings for robust comparisons across types
    const normalizeId = (value) => (value === null || value === undefined ? '' : String(value));

    // Count users per school (robust to string/number id types)
    const usersPerSchool = {};
    users.forEach((user) => {
      const schoolId = normalizeId(user.school_id);
      if (schoolId) {
        usersPerSchool[schoolId] = (usersPerSchool[schoolId] || 0) + 1;
      }
    });

    // Count providers per school using the correct field: accessible_providers
    const providersPerSchool = {};
    schools.forEach((school) => {
      const schoolId = normalizeId(school.id);
      const providerIds = Array.isArray(school.accessible_providers)
        ? [...new Set(school.accessible_providers.map(normalizeId))] // de-dupe just in case
        : [];
      providersPerSchool[schoolId] = providerIds.length;
    });

    // Count provider usage across all schools (how many distinct schools have each provider)
    const providerUsage = {};
    schools.forEach((school) => {
      const providerIds = Array.isArray(school.accessible_providers)
        ? [...new Set(school.accessible_providers.map(normalizeId))]
        : [];
      providerIds.forEach((pid) => {
        providerUsage[pid] = (providerUsage[pid] || 0) + 1;
      });
    });

    // Get top schools by users
    const topSchoolsByUsers = schools
      .map((school) => ({
        ...school,
        userCount: usersPerSchool[normalizeId(school.id)] || 0,
      }))
      .sort((a, b) => b.userCount - a.userCount)
      .slice(0, 5);

    // Get top schools by providers
    const topSchoolsByProviders = schools
      .map((school) => ({
        ...school,
        providerCount: providersPerSchool[normalizeId(school.id)] || 0,
      }))
      .sort((a, b) => b.providerCount - a.providerCount)
      .slice(0, 5);

    // Get most used providers
    const mostUsedProviders = providers
      .map((provider) => ({
        ...provider,
        usageCount: providerUsage[normalizeId(provider.id)] || 0,
      }))
      .sort((a, b) => b.usageCount - a.usageCount)
      .slice(0, 5);

    // Calculate total stats
    const totalUsers = users.length;
    const totalSchools = schools.length;
    const totalProviders = providers.length;
    const averageUsersPerSchool = totalSchools > 0 ? Math.round(totalUsers / totalSchools) : 0;

    return {
      topSchoolsByUsers,
      topSchoolsByProviders,
      mostUsedProviders,
      totalStats: {
        totalSchools,
        totalUsers,
        totalProviders,
        averageUsersPerSchool
      }
    };
  };

  if (loading) {
    return (
      <div className="analytics-content">
        <div className="loading-state">
          <div className="loading-spinner">📊</div>
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-content">
      <div className="content-header">
        <h2>Analytics Dashboard</h2>
        <p>Insights into your school network performance</p>
      </div>

      {/* Total Stats */}
      <div className="analytics-stats-grid">
        <div className="stat-card">
          {/* <div className="stat-icon">🏫</div> */}
          <div className="stat-info">
            <h3>Total Schools</h3>
            <div className="stat-number">{analytics.totalStats.totalSchools}</div>
          </div>
          <div className="overview-stat-icon overview-school-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 3L1 9L12 15L21 10.09V17H23V9M5 13.18V17.18L12 21L19 17.18V13.18L12 17L5 13.18Z"/>
            </svg>
          </div>
        </div>
        <div className="stat-card">
          {/* <div className="stat-icon">👥</div> */}
          <div className="stat-info">
            <h3>Total Users</h3>
            <div className="stat-number">{analytics.totalStats.totalUsers}</div>
          </div>
          <div className="overview-stat-icon overview-student-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"/>
            </svg>
          </div>
        </div>
        <div className="stat-card">
          {/* <div className="stat-icon">🔗</div> */}
          <div className="stat-info">
            <h3>Total Providers</h3>
            <div className="stat-number">{analytics.totalStats.totalProviders}</div>
          </div>
          <div className="overview-stat-icon overview-provider-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM17 12H7V10H17V12ZM15 16H7V14H15V16ZM17 8H7V6H17V8Z"/>
            </svg>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-info">
            <h3>Avg Users/School</h3>
            <div className="stat-number">{analytics.totalStats.averageUsersPerSchool}</div>
          </div>
        </div>
      </div>

      <div className="analytics-grid">
        {/* Top Schools by Users */}
        <div className="analytics-card">
          <div className="analytics-card-header">
            <h3>🏆 Schools with Most Users</h3>
            <p>Top performing schools by user count</p>
          </div>
          <div className="analytics-list">
            {analytics.topSchoolsByUsers.map((school, index) => (
              <div key={school.id} className="analytics-item">
                <div className="rank-badge">#{index + 1}</div>
                <div className="item-info">
                  <h4>{school.name}</h4>
                  <p>{school.location}</p>
                </div>
                <div className="item-stat">
                  <span className="stat-value">{school.userCount}</span>
                  <span className="dashboard-stat-label">users</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Schools by Providers */}
        <div className="analytics-card">
          <div className="analytics-card-header">
            <h3>🔗 Schools with Most Providers</h3>
            <p>Schools with the most integrated providers</p>
          </div>
          <div className="analytics-list">
            {analytics.topSchoolsByProviders.map((school, index) => (
              <div key={school.id} className="analytics-item">
                <div className="rank-badge">#{index + 1}</div>
                <div className="item-info">
                  <h4>{school.name}</h4>
                  <p>{school.location}</p>
                </div>
                <div className="item-stat">
                  <span className="stat-value">{school.providerCount}</span>
                  <span className="dashboard-stat-label">providers</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Most Used Providers */}
        <div className="analytics-card">
          <div className="analytics-card-header">
            <h3>⭐ Most Popular Providers</h3>
            <p>Providers used across the most schools</p>
          </div>
          <div className="analytics-list">
            {analytics.mostUsedProviders.map((provider, index) => (
              <div key={provider.id} className="analytics-item">
                <div className="rank-badge">#{index + 1}</div>
                <div className="item-info">
                  <h4>{provider.name}</h4>
                  <p>{provider.description}</p>
                </div>
                <div className="item-stat">
                  <span className="stat-value">{provider.usageCount}</span>
                  <span className="dashboard-stat-label">schools</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardAnalytics;
